import React, { useState, useRef, useEffect, useCallback } from "react";
import { View, StyleSheet, Pressable, Platform, ViewStyle } from "react-native";
import { autoUpdate, flip, offset as floatingOffset, shift, useFloating } from "@floating-ui/react";
import { Portal } from "react-native-paper";
import { X as XIcon } from "@phosphor-icons/react";
import CustomText from "../general/CustomText/CustomText";
import { colors, radii, spacing, webTransitions } from "../../../styles/design-system";

export type PopoverPlacement =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-start"
  | "top-end"
  | "bottom-start"
  | "bottom-end";

interface PopoverProps {
  /** The element that triggers the popover when clicked or hovered. */
  trigger: React.ReactNode;
  /** Optional title displayed at the top of the popover. */
  title?: string;
  /** Optional subtitle or description text under the title. */
  subtitle?: string;
  /** The rich custom JSX content inside the popover body. */
  children?: React.ReactNode;
  /** Action that triggers the popover. Defaults to "both". */
  triggerType?: "click" | "hover" | "both";
  /** Whether to show a close (X) icon in the top-right corner. Defaults to false. */
  showCloseIcon?: boolean;
  /** Default placement preference. Defaults to "bottom". */
  placement?: PopoverPlacement;
  /** Offset distance from the trigger. Defaults to 8. */
  offset?: number;
  /** Max width of the popover card. Defaults to 320. */
  maxWidth?: number;
  /** Callback called when the popover open state changes. */
  onOpenChange?: (open: boolean) => void;
}

export const Popover = ({
  trigger,
  title,
  subtitle,
  children,
  triggerType = "both",
  showCloseIcon = false,
  placement = "bottom-start",
  offset = 8,
  maxWidth = 340,
  onOpenChange,
}: PopoverProps) => {
  const [open, setOpen] = useState(false);
  const showDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideDelayTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  const { refs, x, y, strategy } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement,
    middleware: [floatingOffset(offset), flip(), shift({ padding: 12 })],
    whileElementsMounted: autoUpdate,
  });

  const clearTimers = () => {
    if (showDelayTimeoutRef.current) clearTimeout(showDelayTimeoutRef.current);
    if (hideDelayTimeoutRef.current) clearTimeout(hideDelayTimeoutRef.current);
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  const handleMouseEnter = () => {
    if (triggerType === "click") return;
    clearTimers();
    showDelayTimeoutRef.current = setTimeout(() => {
      handleOpenChange(true);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (triggerType === "click") return;
    clearTimers();
    hideDelayTimeoutRef.current = setTimeout(() => {
      handleOpenChange(false);
    }, 150);
  };

  const handleToggle = () => {
    if (triggerType === "hover") return;
    handleOpenChange(!open);
  };

  const handleClose = () => {
    handleOpenChange(false);
  };
  const referenceRef = refs.setReference as React.Ref<View>;
  const floatingRef = refs.setFloating as React.Ref<View>;
  return (
    <View style={styles.container}>
      <Pressable
        ref={referenceRef}
        onPress={handleToggle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={styles.trigger}
        accessibilityRole="button">
        {trigger}
      </Pressable>

      {open && (
        <Portal>
          {triggerType !== "hover" && (
            <Pressable style={styles.overlay} onPress={handleClose} accessibilityLabel="Close popover" />
          )}
          <View
            ref={floatingRef}
            style={[
              styles.popoverCard,
              {
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                maxWidth,
              },
            ]}
            onMouseEnter={triggerType !== "click" ? handleMouseEnter : undefined}
            onMouseLeave={triggerType !== "click" ? handleMouseLeave : undefined}>
            <View style={styles.header}>
              <View style={styles.headerTitleArea}>
                {title ? (
                  <CustomText text={title} size="m" weight="bold" useTranslationText={false} style={styles.titleText} />
                ) : null}
                {subtitle ? (
                  <CustomText text={subtitle} size="xs" useTranslationText={false} style={styles.subtitleText} />
                ) : null}
              </View>
              {showCloseIcon && (
                <Pressable
                  onPress={handleClose}
                  style={styles.closeButton}
                  accessibilityRole="button"
                  accessibilityLabel="Close">
                  <XIcon size={16} color={colors.grey[500]} />
                </Pressable>
              )}
            </View>
            {children ? <View style={styles.body}>{children}</View> : null}
          </View>
        </Portal>
      )}
    </View>
  );
};

const fixedOverlayStyle: ViewStyle = {
  position: "fixed" as unknown as ViewStyle["position"],
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9998,
  backgroundColor: "transparent",
};

const styles = StyleSheet.create({
  container: {
    display: "flex",
    flexShrink: 0,
  },
  trigger: {
    alignSelf: "flex-start",
  },
  overlay: {
    ...Platform.select({
      web: fixedOverlayStyle,
      default: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9998,
        backgroundColor: "transparent",
      },
    }),
  },
  popoverCard: {
    position: "absolute",
    backgroundColor: colors.white,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.grey[200],
    padding: spacing.md,
    shadowColor: "#0F172A",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 8,
    zIndex: 10000,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: spacing.sm,
  },
  headerTitleArea: {
    flex: 1,
    gap: spacing.xxs,
  },
  titleText: {
    color: colors.grey[900],
  },
  subtitleText: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  closeButton: {
    padding: spacing.xxs,
    borderRadius: radii.xs,
    alignItems: "center",
    justifyContent: "center",
    ...webTransitions.fast,
  },
  body: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
});
